/**
 * Utilitaires de formatage de dates partagés.
 *
 * formatRelativeTime : temps relatif i18n ("Il y a 2 minutes") + absolu.
 * parseDateFilter : parse une string YYYY-MM-DD en Date.
 */

import i18n from '@/i18n'
import { localeActive } from '@/lib/currency'

import { joursCivilsEcoules } from './joursCivils'

/**
 * `new Intl.DateTimeFormat(...)` est CHER : la construction domine, le formatage
 * est quasi gratuit. Mesuré le 2026-08-11 sur le journal d'audit — facteur **34**
 * entre un formateur reconstruit à chaque ligne et un formateur réutilisé. Une
 * liste de 200 lignes × 2 colonnes de date en construisait 400 par rendu.
 *
 * Les formateurs sont immuables et sans état : un par couple (locale, format)
 * suffit pour toute la durée de vie de l'onglet. Le nombre de clés est borné par
 * le nombre de formats déclarés ici × le nombre de langues — pas de fuite.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>()

/**
 * Formateur de date mutualisé — **à préférer à `new Intl.DateTimeFormat(...)`**
 * partout où le formatage se fait par ligne, par cellule ou par groupe.
 *
 * `locale: undefined` veut dire **la langue ACTIVE de l'application** — jamais
 * celle du navigateur. Cette ligne affirmait exactement le contraire, à six
 * lignes du commentaire qui explique pourquoi c'est faux : un lecteur pressé y
 * lisait l'autorisation d'écrire `toLocaleDateString()` nu.
 */
export function getDateFormatter(
    locale: string | undefined,
    options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
    // ⚠️ `undefined` veut dire « la langue ACTIVE de l'application », jamais
    // celle du navigateur. Passé tel quel à `Intl`, il rendait les dates dans la
    // locale du système : un utilisateur français sur un poste en anglais aurait
    // lu « 03/15/2026 » sous un en-tête français. Même règle que
    // `getNumberFormatter` — il n'existe qu'un endroit où la langue se décide.
    const lang = localeActive(locale)
    const cacheKey = `${lang}|${JSON.stringify(options)}`
    let formatter = formatterCache.get(cacheKey)
    if (!formatter) {
        formatter = new Intl.DateTimeFormat(lang, options)
        formatterCache.set(cacheKey, formatter)
    }
    return formatter
}

/**
 * `lecture` (D160) : la langue du lecteur et le FUSEAU DE L'ORGANISME où écrire la date
 * — l'écran des e-mails envoyés la passe (`useLectureDuJournal`), pour dire la même heure
 * que son export, dans le fuseau de son filtre de dates. Sans elle, la langue active et le poste.
 *
 * D187 : « Hier » et « Il y a N jours » comptent des JOURS CIVILS (`joursCivilsEcoules`), dans
 * le fuseau de la lecture, sinon du poste — jamais des blocs de 24 h. Sous 24 h, les heures.
 */
export function formatRelativeTime(
    iso: string,
    lecture?: { langue: string; fuseau: string },
): { relative: string | null; absolute: string } {
    const d = new Date(iso)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMin = Math.floor(diffMs / 60_000)
    const diffHours = Math.floor(diffMs / 3_600_000)
    const diffDays = joursCivilsEcoules(d, now, lecture?.fuseau)

    // Locale i18n active (pas le navigateur, pas un 'fr-FR' figé) → la date
    // absolue suit la langue choisie (FR/EN), comme `formatBirthDate`.
    const absolute = getDateFormatter(lecture?.langue ?? i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        ...(lecture ? { timeZone: lecture.fuseau } : {}),
    }).format(d)

    if (diffDays >= 7) return { relative: null, absolute }

    let relative: string
    if (diffMin < 1) relative = i18n.t('time.justNow')
    else if (diffMin === 1) relative = i18n.t('time.oneMinuteAgo')
    else if (diffMin < 60) relative = i18n.t('time.minutesAgo', { count: diffMin })
    else if (diffHours === 1) relative = i18n.t('time.oneHourAgo')
    else if (diffHours < 24 || diffDays < 1) relative = i18n.t('time.hoursAgo', { count: diffHours })
    else if (diffDays === 1) relative = i18n.t('time.yesterday')
    else relative = i18n.t('time.daysAgo', { count: diffDays })

    return { relative, absolute }
}

/**
 * Âge TOUJOURS relatif (« il y a 3 semaines », « il y a 2 mois ») + date exacte.
 *
 * Sœur de `formatRelativeTime`, et non un remplacement : celle-ci renvoie
 * `relative: null` au-delà de 7 jours parce que ses appelants (journal d'audit,
 * e-mails envoyés) affichent alors la date seule. Dans un FIL DE VIE, l'inverse
 * est vrai — « il y a 2 mois » situe l'affaire d'un coup d'œil là où une date
 * oblige à compter. Les deux formes coexistent donc, chacune à sa place.
 *
 * `lecture` (D153) : la langue du lecteur et le FUSEAU DE L'ORGANISME où écrire la
 * date exacte — l'historique d'une fiche la passe (`useLectureDuJournal`), comme le
 * journal d'activité. Sans elle, la langue active et le fuseau du poste.
 */
export function formatTimelineAge(
    iso: string,
    lecture?: { langue: string; fuseau: string },
): { relative: string; absolute: string } {
    const d = new Date(iso)
    const diffMs = Date.now() - d.getTime()
    const absolute = getDateFormatter(lecture?.langue ?? i18n.language, {
        dateStyle: 'long',
        timeStyle: 'short',
        ...(lecture ? { timeZone: lecture.fuseau } : {}),
    }).format(d)

    // Un horodatage à venir (échéance, décalage d'horloge) ne dit pas « il y a ».
    if (diffMs < 0) return { relative: i18n.t('time.justNow'), absolute }

    const min = Math.floor(diffMs / 60_000)
    const hours = Math.floor(diffMs / 3_600_000)
    // D187 — des JOURS CIVILS, comme `formatRelativeTime` (même fonction, même fuseau).
    const days = joursCivilsEcoules(d, new Date(Date.now()), lecture?.fuseau)

    let relative: string
    if (min < 1) relative = i18n.t('time.justNow')
    else if (min === 1) relative = i18n.t('time.oneMinuteAgo')
    else if (min < 60) relative = i18n.t('time.minutesAgo', { count: min })
    else if (hours === 1) relative = i18n.t('time.oneHourAgo')
    else if (hours < 24 || days < 1) relative = i18n.t('time.hoursAgo', { count: hours })
    else if (days === 1) relative = i18n.t('time.yesterday')
    else if (days < 7) relative = i18n.t('time.daysAgo', { count: days })
    else if (days < 31) relative = i18n.t('time.weeksAgo', { count: Math.floor(days / 7) })
    else if (days < 365) relative = i18n.t('time.monthsAgo', { count: Math.floor(days / 30) })
    else relative = i18n.t('time.yearsAgo', { count: Math.floor(days / 365) })

    return { relative, absolute }
}

export function parseDateFilter(value?: string): Date | null {
    if (!value) return null
    const d = new Date(value + 'T00:00:00')
    return isNaN(d.getTime()) ? null : d
}

/**
 * Formate une date de naissance ISO (YYYY-MM-DD) en format local (DD/MM/YYYY en FR).
 * Retourne "—" si null/invalide.
 */
/**
 * Formate une date CALENDAIRE (`YYYY-MM-DD`, sans fuseau — affiliations, échéances)
 * en format LOCAL, jamais en ISO brut. Parse en date locale (pas `new Date(iso)` qui
 * interprète en UTC minuit → décalage d'un jour dans les fuseaux négatifs). Repli sur
 * la chaîne brute si le format est inattendu.
 */
export function formatCalendarDate(iso: string | null | undefined, locale: string): string {
    if (!iso) return '—'
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
    if (!m) return iso
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    return getDateFormatter(locale, { dateStyle: 'medium' }).format(d)
}

/**
 * Formate un HORODATAGE (`timestamptz` — `*_at`) dans la langue ACTIVE.
 *
 * 🔴 **Distinct de `formatCalendarDate`, et il faut choisir.** Celui-ci
 * convertit vers le fuseau du lecteur ; l'autre découpe les dix premiers
 * caractères par expression régulière, sans aucune conversion. Passer un
 * `closed_at` à 23 h 30 heure de Paris à `formatCalendarDate` affiche la
 * VEILLE — silencieusement.
 *
 * ⚠️ Il existe parce que six écrans appelaient `new Date(iso).toLocaleDateString()`
 * SANS argument de locale : sur un poste dont le navigateur est en `en-US` —
 * courant en parc d'entreprise — l'interface française affichait « 9/5/2026 »
 * là où l'utilisateur attendait « 05/09/2026 ». Sur une preuve horodatée,
 * 9 mai et 5 septembre ne se distinguent plus, et rien ne signale l'écart.
 */
export function formatInstant(
    iso: string | null | undefined,
    locale: string,
    options: { withTime?: boolean } = {},
): string {
    if (!iso) return '—'
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return getDateFormatter(locale, {
        dateStyle: 'medium',
        ...(options.withTime ? { timeStyle: 'short' as const } : {}),
    }).format(d)
}

export function formatBirthDate(iso: string | null | undefined, locale: string): string {
    if (!iso) return '—'
    try {
        const d = new Date(iso)
        if (isNaN(d.getTime())) return iso
        return getDateFormatter(locale, { dateStyle: 'medium' }).format(d)
    } catch {
        return iso
    }
}

/**
 * Convention Argalis — durée écoulée depuis une date.
 *   < 1 mois      → "X jours"
 *   < 1 an        → "X mois et Y jours"
 *   ≥ 1 an        → "X an(s) et Y mois" (sans les jours)
 *
 * Retourne une string formatée (i18n-aware via la clé passée dans opts.locale — défaut français).
 * Passer null/undefined → "—".
 */
export function formatElapsed(iso: string | null | undefined): string {
    if (!iso) return '—'
    const from = new Date(iso)
    const now = new Date()
    if (isNaN(from.getTime())) return '—'

    // Diff en **jours calendaires** (pas en blocs de 24h glissantes) : on
    // compare les dates locales à minuit. Sinon bloqué hier 22h → maintenant
    // 8h donnait 0 jour (10h glissantes), au lieu du 1 jour attendu par
    // l'utilisateur (changement de date calendaire). Bug 2026-05-17.
    const fromDayUtc = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())
    const nowDayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
    const totalDays = Math.round((nowDayUtc - fromDayUtc) / 86_400_000)
    if (totalDays < 0) return '—'

    if (totalDays < 30) {
        return totalDays <= 1
            ? i18n.t('time.elapsed.daysSmall', { count: totalDays })
            : i18n.t('time.elapsed.days', { count: totalDays })
    }

    // Approx months (30.4375 = 365.25 / 12). Precise enough for UI.
    const totalMonths = Math.floor(totalDays / 30.4375)
    if (totalMonths < 12) {
        const months = totalMonths
        const remDays = Math.max(0, totalDays - Math.floor(months * 30.4375))
        if (remDays === 0) return i18n.t('time.elapsed.monthsOnly', { count: months })
        return i18n.t('time.elapsed.monthsAndDays', { months, days: remDays })
    }

    const years = Math.floor(totalMonths / 12)
    const remMonths = totalMonths - years * 12
    if (remMonths === 0) return i18n.t('time.elapsed.yearsOnly', { count: years })
    // `count` est obligatoire pour que i18next sélectionne la bonne variante
    // pluralisée (`yearsAndMonths_one` vs `_other`). Sans lui, la clé brute
    // « time.elapsed.yearsAndMonths » est rendue à l'écran (bug 2026-05-04).
    return i18n.t('time.elapsed.yearsAndMonths', {
        count: years,
        years,
        months: remMonths,
    })
}

/**
 * Conversions entre la valeur STOCKÉE d'un champ date (`YYYY-MM-DD`) et l'objet
 * `Date` attendu par `<DatePicker>` (design system).
 *
 * Ancrage à MINUIT LOCAL, jamais en UTC : `new Date('1985-03-12')` est interprété
 * en UTC et recule d'un jour dans les fuseaux négatifs — une date de naissance
 * deviendrait le 11 mars. Même contrat que les filtres de DataTable.
 */
export function dateInputToDate(value: unknown): Date | null {
  const s = typeof value === 'string' ? value.trim() : '';
  return s ? new Date(`${s.slice(0, 10)}T00:00:00`) : null;
}

/** `Date` → `YYYY-MM-DD` (chaîne vide si aucune date). */
export function dateToDateInput(d: Date | undefined): string {
  if (!d) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}
/**
 * Le jour d'AUJOURD'HUI chez le lecteur, au format `YYYY-MM-DD`.
 *
 * `new Date().toISOString().slice(0, 10)` — l'idiome qu'on remplace — rend la VEILLE
 * entre minuit et 02 h 00 heure de Paris, puisqu'il lit le jour de Greenwich. Une
 * affiliation ouverte à 00 h 30 démarrait donc la veille, et un barème de
 * commissionnement créé à 01 h 00 s'appliquait rétroactivement d'un jour.
 *
 * Ce défaut est étroit dans le temps, mais il tombe précisément quand personne ne
 * regarde — et il est indétectable en journée, ce qui le rend impossible à reproduire
 * pour qui le signale.
 */
export function todayDateInput(): string {
  return dateToDateInput(new Date());
}

/** `toISODate` / `fromISODate` — le pont `Date` ↔ `'YYYY-MM-DD'` local (`date-utils.iso.ts`). */
export { fromISODate, toISODate } from './date-utils.iso'
